// Serviço para detectar tipo de dispositivo e estratégias de localização

import { logger } from '../utils/logger';

export interface DeviceInfo {
    isDesktop: boolean;
    isMobile: boolean;
    isTablet: boolean;
    platform: string;
    hasGPS: boolean;
    hasTouchScreen: boolean;
    userAgent: string;
    connectionType?: string;
}

export interface LocationStrategy {
    method: 'gps' | 'ip' | 'wifi' | 'manual';
    accuracy: 'high' | 'medium' | 'low';
    description: string;
    icon: string;
}

// Detecta informações do dispositivo
export function getDeviceInfo(): DeviceInfo {
    const userAgent = navigator.userAgent.toLowerCase();
    const platform = navigator.platform.toLowerCase();

    // Detecta se é mobile
    const isMobile = /android|iphone|ipod|blackberry|windows phone|mobile/.test(userAgent) ||
        ('ontouchstart' in window && window.matchMedia('(max-width: 768px)').matches);

    // Detecta se é tablet
    const isTablet = /ipad|tablet|kindle|playbook|silk/.test(userAgent) ||
        ('ontouchstart' in window && window.matchMedia('(min-width: 769px) and (max-width: 1024px)').matches);

    // Desktop se não for mobile nem tablet
    const isDesktop = !isMobile && !isTablet;

    // Verifica se tem GPS (aproximadamente)
    const hasGPS = isMobile || isTablet || 'geolocation' in navigator;

    // Verifica se tem tela touch
    const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    // Tipo de conexão (se disponível)
    let connectionType = 'unknown';
    if ('connection' in navigator) {
        connectionType = (navigator as any).connection?.effectiveType || 'unknown';
    }

    return {
        isDesktop,
        isMobile,
        isTablet,
        platform,
        hasGPS,
        hasTouchScreen,
        userAgent,
        connectionType
    };
}

// Estratégias de localização por tipo de dispositivo
export function getLocationStrategies(deviceInfo: DeviceInfo): LocationStrategy[] {
    const strategies: LocationStrategy[] = [];

    if (deviceInfo.isMobile || deviceInfo.isTablet) {
        strategies.push({
            method: 'gps',
            accuracy: 'high',
            description: 'GPS do dispositivo móvel',
            icon: 'location'
        });
    }

    if (deviceInfo.isDesktop) {
        strategies.push(
            {
                method: 'wifi',
                accuracy: 'medium',
                description: 'Localização por Wi-Fi/Rede',
                icon: 'wifi'
            },
            {
                method: 'ip',
                accuracy: 'low',
                description: 'Localização por IP',
                icon: 'globe'
            }
        );
    }

    // GPS como fallback para desktop (alguns têm)
    if (deviceInfo.isDesktop && 'geolocation' in navigator) {
        strategies.push({
            method: 'gps',
            accuracy: 'medium',
            description: 'GPS do navegador (limitado)',
            icon: 'location'
        });
    }

    // Manual como último recurso
    strategies.push({
        method: 'manual',
        accuracy: 'high',
        description: 'Inserir localização manualmente',
        icon: 'input'
    });

    return strategies;
}

// Função para verificar conectividade básica
async function checkConnectivity(): Promise<boolean> {
    try {
        // Tenta uma requisição simples para um serviço confiável
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000);

        const response = await fetch('https://httpbin.org/get', {
            signal: controller.signal,
            method: 'HEAD', // Só precisamos verificar se conseguimos conectar
            cache: 'no-cache'
        });

        clearTimeout(timeoutId);
        return response.ok;
    } catch (error) {
        logger.warn('🔌 Teste de conectividade falhou:', error);
        return false;
    }
}

// Lista de serviços de geolocalização por IP com fallbacks múltiplos
const IP_GEOLOCATION_SERVICES = [
    {
        url: 'https://ipinfo.io/json',
        parser: (data: any) => {
            if (data.loc) {
                const [lat, lon] = data.loc.split(',');
                return {
                    latitude: parseFloat(lat),
                    longitude: parseFloat(lon),
                    isValid: !!(lat && lon)
                };
            }
            return { latitude: 0, longitude: 0, isValid: false };
        },
        name: 'ipinfo.io'
    },
    {
        url: 'https://ipapi.co/json/',
        parser: (data: any) => ({
            latitude: parseFloat(data.latitude),
            longitude: parseFloat(data.longitude),
            isValid: !!(data.latitude && data.longitude)
        }),
        name: 'ipapi.co'
    },
    {
        url: 'https://api.db-ip.com/v2/free/self',
        parser: (data: any) => ({
            latitude: parseFloat(data.ipAddress ? data.latitude : data.lat),
            longitude: parseFloat(data.ipAddress ? data.longitude : data.lon),
            isValid: !!(data.latitude || data.lat) && !!(data.longitude || data.lon)
        }),
        name: 'db-ip.com'
    }
];

// Função para fazer uma tentativa de requisição com timeout e retry
async function tryFetchWithRetry(url: string, retries = 2, timeout = 8000): Promise<any> {
    for (let attempt = 0; attempt <= retries; attempt++) {
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), timeout);

            logger.log(`🌐 Tentativa ${attempt + 1}/${retries + 1} para ${url}`);

            const response = await fetch(url, {
                signal: controller.signal,
                method: 'GET',
                headers: {
                    'Accept': 'application/json'
                }
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            logger.log(`✅ Sucesso na requisição para ${url}`);
            return data;

        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);

            // Detecta problemas específicos
            if (errorMessage.includes('CORS') || errorMessage.includes('cors')) {
                logger.warn(`🚫 Problema de CORS detectado em ${url}, pulando...`);
                throw new Error(`CORS_ERROR: ${errorMessage}`);
            }

            logger.warn(`❌ Tentativa ${attempt + 1} falhou para ${url}:`, errorMessage);

            if (attempt === retries) {
                throw error;
            }

            // Aguarda um pouco antes da próxima tentativa
            await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
        }
    }
}

// Função para obter localização por IP (usando múltiplos serviços com fallbacks)
export async function getLocationByIP(): Promise<{ latitude: number, longitude: number, accuracy: number }> {
    logger.log('🔍 Iniciando localização por IP...');

    // Verifica conectividade básica primeiro
    const isConnected = await checkConnectivity();
    if (!isConnected) {
        logger.warn('🔌 Sem conectividade detectada, usando localização padrão');
        return {
            latitude: -23.5505,
            longitude: -46.6333,
            accuracy: 50000
        };
    }

    // Tenta cada serviço em ordem
    for (const service of IP_GEOLOCATION_SERVICES) {
        try {
            logger.log(`🌐 Tentando serviço: ${service.name}`);

            const data = await tryFetchWithRetry(service.url);
            const parsed = service.parser(data);

            if (parsed.isValid) {
                logger.log(`✅ Localização obtida via ${service.name}:`, {
                    lat: parsed.latitude,
                    lon: parsed.longitude
                });

                return {
                    latitude: parsed.latitude,
                    longitude: parsed.longitude,
                    accuracy: 10000 // Precisão baixa para IP (10km)
                };
            } else {
                logger.warn(`⚠️ Resposta inválida de ${service.name}:`, data);
            }

        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);

            if (errorMessage.includes('CORS_ERROR')) {
                logger.warn(`🚫 Problema de CORS em ${service.name}, tentando próximo serviço...`);
            } else {
                logger.error(`❌ Falha no serviço ${service.name}:`, errorMessage);
            }
            continue;
        }
    }

    // Se chegou até aqui, todos os serviços falharam
    logger.error('💥 Todos os serviços de localização por IP falharam');

    // Fallback para localização padrão (São Paulo, Brasil) como último recurso
    logger.log('🏙️ Usando localização padrão (São Paulo)');
    return {
        latitude: -23.5505,
        longitude: -46.6333,
        accuracy: 50000 // Precisão muito baixa
    };
}// Função para obter localização com Wi-Fi (usando API do navegador com configurações especiais)
export async function getLocationByWiFi(): Promise<GeolocationPosition> {
    return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
            reject(new Error('Geolocalização não suportada'));
            return;
        }

        // Configurações progressivas para melhor compatibilidade (timeouts aumentados)
        const attempts = [
            {
                name: 'Rápida (Wi-Fi)',
                options: {
                    enableHighAccuracy: false,
                    timeout: 20000, // Aumentado de 10s para 20s
                    maximumAge: 300000
                }
            },
            {
                name: 'Média (GPS baixa precisão)',
                options: {
                    enableHighAccuracy: false,
                    timeout: 45000, // Aumentado de 20s para 45s  
                    maximumAge: 60000
                }
            },
            {
                name: 'Lenta (GPS alta precisão)',
                options: {
                    enableHighAccuracy: true,
                    timeout: 90000, // Aumentado de 30s para 90s (1.5 min)
                    maximumAge: 0
                }
            }
        ];

        let currentAttempt = 0;

        const tryGeolocation = () => {
            if (currentAttempt >= attempts.length) {
                reject(new Error('Todas as tentativas de geolocalização falharam'));
                return;
            }

            const attempt = attempts[currentAttempt];
            logger.log(`🔍 Tentativa ${currentAttempt + 1}/${attempts.length}: ${attempt.name}`);

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    logger.log(`✅ Sucesso na tentativa ${currentAttempt + 1}: ${attempt.name}`);
                    resolve(position);
                },
                (error) => {
                    logger.warn(`❌ Tentativa ${currentAttempt + 1} falhou:`, {
                        code: error.code,
                        message: error.message,
                        attempt: attempt.name
                    });

                    currentAttempt++;

                    // Pequeno delay antes da próxima tentativa
                    setTimeout(tryGeolocation, 1000);
                },
                attempt.options
            );
        };

        tryGeolocation();
    });
}// Função principal para obter localização com estratégias múltiplas
export async function getLocationWithStrategies(): Promise<{
    latitude: number;
    longitude: number;
    accuracy: number;
    method: string;
    deviceInfo: DeviceInfo;
}> {
    const deviceInfo = getDeviceInfo();
    const strategies = getLocationStrategies(deviceInfo);
    const isProd = import.meta.env.PROD;
    const errors: string[] = [];

    logger.log('Environment:', isProd ? 'production' : 'development');
    logger.log('Protocol:', location.protocol);
    logger.log('Device Info:', deviceInfo);
    logger.log('Available strategies:', strategies);

    // Em produção, otimiza estratégias baseado no tipo de dispositivo
    if (isProd) {
        if (deviceInfo.isDesktop) {
            // Para desktop: IP primeiro (mais rápido), depois Wi-Fi
            const optimizedStrategies: LocationStrategy[] = [
                {
                    method: 'ip',
                    accuracy: 'low',
                    description: 'Localização por IP (rápida)',
                    icon: 'globe'
                },
                {
                    method: 'wifi',
                    accuracy: 'medium',
                    description: 'Localização por Wi-Fi/Rede',
                    icon: 'wifi'
                }
            ];
            strategies.splice(0, strategies.length, ...optimizedStrategies);
            logger.log('�️ PROD: Estratégias otimizadas para desktop');
        } else {
            // Para mobile: GPS primeiro, depois IP como fallback
            const mobileStrategies: LocationStrategy[] = [
                {
                    method: 'gps',
                    accuracy: 'high',
                    description: 'GPS do dispositivo móvel',
                    icon: 'location'
                },
                {
                    method: 'ip',
                    accuracy: 'low',
                    description: 'Localização por IP (fallback)',
                    icon: 'globe'
                }
            ];
            strategies.splice(0, strategies.length, ...mobileStrategies);
            logger.log('📱 PROD: Estratégias otimizadas para mobile');
        }
    }

    // Tenta cada estratégia em ordem de preferência
    for (let i = 0; i < strategies.length; i++) {
        const strategy = strategies[i];

        try {
            logger.log(`🔄 Tentativa ${i + 1}/${strategies.length}: ${strategy.description}`);

            switch (strategy.method) {
                case 'gps':
                    if (deviceInfo.isMobile || deviceInfo.isTablet) {
                        // Para móveis, usa configurações progressivas (timeouts aumentados)
                        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
                            const mobileOptions = {
                                enableHighAccuracy: true,
                                timeout: isProd ? 30000 : 25000, // Aumentado significativamente
                                maximumAge: isProd ? 120000 : 60000
                            };

                            // Primeira tentativa com alta precisão
                            navigator.geolocation.getCurrentPosition(
                                resolve,
                                (_error) => {
                                    logger.warn('GPS alta precisão falhou, tentando baixa precisão...');
                                    // Fallback com baixa precisão
                                    const fallbackOptions = {
                                        enableHighAccuracy: false,
                                        timeout: 60000, // Aumentado de 20s para 60s
                                        maximumAge: 300000
                                    };
                                    navigator.geolocation.getCurrentPosition(resolve, reject, fallbackOptions);
                                },
                                mobileOptions
                            );
                        });

                        return {
                            latitude: position.coords.latitude,
                            longitude: position.coords.longitude,
                            accuracy: position.coords.accuracy || 50,
                            method: 'GPS (móvel)',
                            deviceInfo
                        };
                    } else {
                        // Para desktop, usa a função Wi-Fi otimizada
                        const position = await getLocationByWiFi();
                        return {
                            latitude: position.coords.latitude,
                            longitude: position.coords.longitude,
                            accuracy: position.coords.accuracy || 1000,
                            method: 'GPS (desktop)',
                            deviceInfo
                        };
                    }
                    break;

                case 'wifi':
                    const wifiPosition = await getLocationByWiFi();
                    return {
                        latitude: wifiPosition.coords.latitude,
                        longitude: wifiPosition.coords.longitude,
                        accuracy: wifiPosition.coords.accuracy || 1000,
                        method: 'Wi-Fi/Rede',
                        deviceInfo
                    };
                    break;

                case 'ip':
                    logger.log('🌐 Iniciando localização por IP...');
                    const ipLocation = await getLocationByIP();
                    logger.log('✅ Localização por IP obtida:', ipLocation);
                    return {
                        latitude: ipLocation.latitude,
                        longitude: ipLocation.longitude,
                        accuracy: ipLocation.accuracy,
                        method: 'Localização por IP',
                        deviceInfo
                    };
                    break;

                case 'manual':
                    // Este caso será tratado na UI
                    throw new Error('MANUAL_INPUT_REQUIRED');
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            const isLastStrategy = i === strategies.length - 1;

            logger.warn(`⚠️ Estratégia ${strategy.method} falhou:`, errorMessage);
            errors.push(`${strategy.method}: ${errorMessage}`);

            // Logs detalhados para debugging em produção
            if (isProd) {
                logger.error(`[PROD DEBUG] Falha na estratégia ${strategy.method}:`, {
                    error: errorMessage,
                    protocol: location.protocol,
                    userAgent: navigator.userAgent,
                    geolocationSupported: 'geolocation' in navigator,
                    timestamp: new Date().toISOString()
                });
            }

            // Se não é a última estratégia, continua
            if (!isLastStrategy) {
                logger.log(`⏭️ Tentando próxima estratégia...`);
                continue;
            }
        }
    }

    // Se chegou até aqui, todas as estratégias falharam
    const errorSummary = `Todas as estratégias falharam: ${errors.join('; ')}`;
    logger.error('💥 Falha completa:', errorSummary);

    // Retorna erro mais informativo baseado no contexto
    if (errors.some(e => e.includes('Permission denied') || e.includes('denied'))) {
        throw new Error('Permissão de localização negada. Clique no ícone de localização e permita o acesso.');
    } else if (errors.some(e => e.includes('TIMEOUT') || e.includes('timeout'))) {
        throw new Error('Tempo limite excedido. Verifique sua conexão e tente novamente.');
    } else if (location.protocol !== 'https:' && !location.hostname.includes('localhost')) {
        throw new Error('Localização requer conexão segura (HTTPS).');
    } else {
        throw new Error('Localização indisponível. Tente ativar o GPS/Wi-Fi ou recarregar a página.');
    }
}

// Função para verificar quais estratégias estão disponíveis
export function getAvailableStrategies(): {
    gps: boolean;
    wifi: boolean;
    ip: boolean;
    manual: boolean;
} {
    const deviceInfo = getDeviceInfo();

    return {
        gps: 'geolocation' in navigator,
        wifi: 'geolocation' in navigator && deviceInfo.isDesktop,
        ip: typeof fetch !== 'undefined',
        manual: true // Sempre disponível
    };
}